import React, { useEffect, useState, useRef } from 'react';
import { Clock, Star, AlertCircle } from 'lucide-react';
import { ScrollReveal } from './useScrollReveal';
function ProgressBar() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        obs.unobserve(el);
      }
    }, {
      threshold: 0.1
    });
    obs.observe(el);
    return () => {
      if (el) obs.unobserve(el);
    };
  }, []);
  return <div ref={ref} className="h-3 bg-navy-900 rounded-full overflow-hidden border border-navy-700" data-id="element-205">
      <div className="h-full bg-gradient-to-r from-cosmic-500 to-purple-400 rounded-full relative transition-all duration-[1500ms] ease-out" style={{
      width: visible ? '82%' : '0%'
    }} data-id="element-206">
        <div className="absolute inset-0 bg-white/20 animate-[pulse_2s_ease-in-out_infinite]" data-id="element-207" />
      </div>
    </div>;
}
export function CountdownTimer() {
  // Start from 23:54:55
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 54,
    seconds: 55
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const pad = (num: number) => num.toString().padStart(2, '0');
  return <div className="flex items-center justify-center gap-2 text-3xl md:text-4xl font-bold font-poppins text-white" data-id="element-208">
      <div className="bg-navy-900 px-4 py-2 rounded-lg border border-navy-700 min-w-[80px] text-center" data-id="element-209">
        {pad(timeLeft.hours)}
        <span className="text-sm block text-slate-400 font-normal mt-1" data-id="element-210">
          hs
        </span>
      </div>
      <span className="text-cosmic-500 pb-6" data-id="element-211">:</span>
      <div className="bg-navy-900 px-4 py-2 rounded-lg border border-navy-700 min-w-[80px] text-center" data-id="element-212">
        {pad(timeLeft.minutes)}
        <span className="text-sm block text-slate-400 font-normal mt-1" data-id="element-213">
          min
        </span>
      </div>
      <span className="text-cosmic-500 pb-6" data-id="element-214">:</span>
      <div className="bg-navy-900 px-4 py-2 rounded-lg border border-navy-700 min-w-[80px] text-center" data-id="element-215">
        {pad(timeLeft.seconds)}
        <span className="text-sm block text-slate-400 font-normal mt-1" data-id="element-216">
          seg
        </span>
      </div>
    </div>;
}
export function PricingSection() {
  return <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-800 relative cv-auto" data-id="element-217">
      <div className="max-w-5xl mx-auto" data-id="element-218">
        {/* Urgency Header */}
        <div className="text-center mb-12" data-id="element-219">
          <div className="inline-flex items-center gap-2 text-cosmic-400 font-bold text-lg mb-6 bg-cosmic-500/10 px-4 py-2 rounded-full border border-cosmic-500/20" data-id="element-220">
            <Clock className="w-5 h-5 animate-pulse" data-id="element-221" />
            PRECIO DE LANZAMIENTO — CIERRA EN:
          </div>

          <CountdownTimer data-id="element-222" />

          <div className="mt-6 flex items-center justify-center gap-2 text-yellow-400 font-medium" data-id="element-223">
            <AlertCircle className="w-5 h-5" data-id="element-224" />
            Después sube a su precio normal. No vuelve.
          </div>

          <div className="mt-8 max-w-md mx-auto" data-id="element-225">
            <div className="flex justify-between text-sm text-slate-300 mb-2" data-id="element-226">
              <span data-id="element-227">Cupos al precio de lanzamiento</span>
              <span className="font-bold text-cosmic-400" data-id="element-228">Quedan pocos</span>
            </div>
            <ProgressBar data-id="element-229" />
            <p className="text-xs text-slate-400 mt-2 text-right" data-id="element-230">
              82% de los cupos ya tomados
            </p>
          </div>
        </div>

        <h2 className="text-3xl font-bold text-center mb-10" data-id="element-231">
          ELEGÍ TU MEGAPACK
        </h2>

        <div className="grid md:grid-cols-2 gap-8 items-stretch" data-id="element-232">
          {/* Standard Pack */}
          <ScrollReveal direction="up" className="bg-navy-900 border border-navy-700 rounded-3xl p-8 flex flex-col relative hover:border-blue-500/50 transition-colors" data-id="element-233">
            <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-id="element-234">
              💙 MEGAPACK MI ESPACIO INTERIOR
            </h3>
            <p className="text-blue-400 font-medium mb-6" data-id="element-235">6 Tomos + 6 Bonos</p>

            <div className="bg-navy-800 rounded-xl p-4 mb-8 text-sm text-slate-300" data-id="element-236">
              <span className="font-semibold text-white" data-id="element-237">Ideal para:</span>{' '}
              Padres, madres y docentes que quieren acompañar el desarrollo
              emocional de adolescentes de 12 a 18 años
            </div>

            <div className="mt-auto" data-id="element-238">
              <div className="flex items-end gap-3 mb-6" data-id="element-239">
                <span className="text-2xl text-slate-500 line-through font-medium" data-id="element-240">
                  $65 USD
                </span>
                <span className="text-5xl font-bold text-white" data-id="element-241">$9.99</span>
              </div>

              <button onClick={() => window.open('https://recursopack.com/cart/40978751914033:1', '_blank')} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-6 rounded-xl transition-colors flex justify-center items-center gap-2 cursor-pointer" data-id="element-242">
                → QUIERO MI MEGAPACK AHORA
              </button>
              <p className="text-center text-xs text-slate-400 mt-2" data-id="element-243">
                🌎 Pagalo en tu moneda local
              </p>
            </div>
          </ScrollReveal>

          {/* Master Pack (Recommended) */}
          <ScrollReveal direction="up" delay={2} className="bg-gradient-to-b from-navy-800 to-navy-900 border-2 border-gold-500 rounded-3xl p-8 flex flex-col relative transform md:-translate-y-4 shadow-[0_0_40px_rgba(234,179,8,0.15)]" data-id="element-244">
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gold-500 text-navy-900 font-bold px-4 py-1 rounded-full text-sm flex items-center gap-1 shadow-lg" data-id="element-245">
              <Star className="w-4 h-4 fill-navy-900" data-id="element-246" /> MÁS ELEGIDO
            </div>

            <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-id="element-247">
              🏆 MEGAPACK + MASTER-PACK
            </h3>
            <p className="text-gold-400 font-medium mb-6" data-id="element-248">
              6 Tomos + 6 Bonos + Sistema Completo de Implementación
            </p>

            <div className="bg-navy-900/50 border border-gold-500/20 rounded-xl p-4 mb-8 text-sm text-slate-300" data-id="element-249">
              <span className="font-semibold text-white" data-id="element-250">Ideal para:</span>{' '}
              Familias y docentes que quieren resultados desde el primer día —
              con juegos, cartas, tokens, misiones y sistema de 30 días
            </div>

            <div className="mt-auto" data-id="element-251">
              <div className="flex items-end gap-3 mb-6" data-id="element-252">
                <span className="text-2xl text-slate-500 line-through font-medium" data-id="element-253">
                  $95 USD
                </span>
                <span className="text-5xl font-bold text-white" data-id="element-254">$14.99</span>
              </div>

              <button onClick={() => window.open('https://recursopack.com/cart/40978752340017:1', '_blank')} className="w-full bg-gradient-to-r from-gold-500 to-yellow-400 hover:from-gold-400 hover:to-yellow-300 text-navy-900 font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 shadow-[0_0_20px_rgba(234,179,8,0.4)] flex justify-center items-center gap-2 cursor-pointer" data-id="element-255">
                → QUIERO EL MEGAPACK COMPLETO
              </button>
              <p className="text-center text-xs text-slate-400 mt-2" data-id="element-256">
                🌎 Pagalo en tu moneda local
              </p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>;
}